/**
 * CrawlProgressStep
 *
 * Step 3b: Shows live crawl progress when user selects crawl-based onboarding.
 * Part of AEO Onboarding Wizard - Crawl-Based Path
 *
 * Date: February 8, 2026
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  Globe,
  Loader2,
  CheckCircle,
  FileSearch,
  Package,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CrawlProgress {
  status: string;
  pagesCrawled: number;
  productsFound: number;
  maxPages: number;
  currentUrl?: string;
  pagesAnalysed?: number;
  pagesClassified?: number;
  classificationTotal?: number;
}

interface CrawlProgressStepProps {
  progress: CrawlProgress | null;
  error?: string | null;
  onRetry?: () => void;
  isLoading?: boolean;
}

export function CrawlProgressStep({
  progress,
  error,
  onRetry,
  isLoading
}: CrawlProgressStepProps) {
  const isRunning = progress?.status === 'running' || isLoading;
  const isCompleted = progress?.status === 'completed';
  const isFailed = progress?.status === 'failed' || !!error;

  const pageProgress = progress?.maxPages
    ? Math.min((progress.pagesCrawled / progress.maxPages) * 100, 100)
    : 0;

  // Classification phase (#4803 item 2): once the crawl bar fills, the backend classifies pages
  // for several minutes. Without this the screen froze at 100% exactly when the pipeline was
  // busiest — indistinguishable from a hang.
  const classificationTotal = progress?.classificationTotal || 0;
  const pagesClassified = progress?.pagesClassified || 0;
  const isClassifying = isRunning && classificationTotal > 0 && pagesClassified < classificationTotal;
  const classificationProgress = classificationTotal > 0
    ? Math.min((pagesClassified / classificationTotal) * 100, 100)
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isRunning && <Globe className="h-5 w-5 animate-pulse text-blue-600" />}
          {isCompleted && <CheckCircle className="h-5 w-5 text-green-600" />}
          {isFailed && <AlertCircle className="h-5 w-5 text-red-600" />}
          Crawling Your Website
        </CardTitle>
        <CardDescription>
          {isRunning && (isClassifying
            ? `Analyzing crawled pages to identify products… (${pagesClassified} of ${classificationTotal})`
            : 'Discovering and extracting products from your website...')}
          {isCompleted && 'Website crawl completed successfully'}
          {isFailed && 'Crawl encountered an error'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Pages Crawled Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-muted-foreground" />
              Pages Crawled
            </span>
            <span className="font-medium">
              {progress?.pagesCrawled || 0} / {progress?.maxPages || 100}
            </span>
          </div>
          <Progress value={pageProgress} className="h-2" />
        </div>

        {/* Classification Progress (appears once pages start being analyzed) */}
        {classificationTotal > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Loader2 className={`h-4 w-4 text-muted-foreground ${isClassifying ? 'animate-spin' : ''}`} />
                Pages Analyzed
              </span>
              <span className="font-medium">
                {pagesClassified} / {classificationTotal}
              </span>
            </div>
            <Progress value={classificationProgress} className="h-2" />
          </div>
        )}

        {/* Products Found Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              Products Found
            </span>
            <span className="font-medium">
              {progress?.productsFound || 0}
              <span className="text-muted-foreground text-xs"> (up to 50 will be imported)</span>
            </span>
          </div>
        </div>

        {/* Current URL */}
        {progress?.currentUrl && isRunning && (
          <div className="rounded-md bg-muted p-3">
            <p className="text-xs text-muted-foreground mb-1">Currently processing:</p>
            <p className="text-sm font-mono truncate">{progress.currentUrl}</p>
          </div>
        )}

        {/* Status Badge */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            {isRunning && (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <Badge variant="secondary">In Progress</Badge>
              </>
            )}
            {isCompleted && <Badge variant="outline" className="text-green-600 border-green-600">Completed</Badge>}
            {isFailed && <Badge variant="destructive">Failed</Badge>}
          </div>

          {isFailed && onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          )}
        </div>

        {/* Sitemap Info */}
        {isCompleted && (
          <div className="rounded-md border p-4 space-y-2">
            <h4 className="font-medium text-sm">Crawl Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Pages Crawled</p>
                <p className="font-medium">{progress?.pagesCrawled || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Products Extracted</p>
                <p className="font-medium">{progress?.productsFound || 0}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
